import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Gift, LockKey, WarningCircle } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATE_TEXT = {
  link_expired: (g) => `This link has expired. Ask ${g.from_name} to resend your gift. The gift itself is still waiting for you.`,
  redeemed: () => "This gift has already been claimed. Each gift can only be used once.",
  expired: () => "This gift has expired.",
  refunded: () => "This gift is no longer available.",
  on_hold: () => "This gift is on hold for now. Please try again later.",
};

const Notice = ({ text, testid }) => (
  <div data-testid={testid} className="flex gap-3 rounded-2xl bg-[#FDF7EB] p-5 text-[#4A4540]">
    <WarningCircle size={26} weight="duotone" className="shrink-0 text-[#C58F28]" /><p>{text}</p>
  </div>
);

export default function ClaimGift() {
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [gift, setGift] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (token.length < 20) { setError("This gift link isn't valid. Please open the link from your gift email, or enter the code on the Redeem page."); return; }
    api.get(`/gifts/claim/${encodeURIComponent(token)}`).then((r) => setGift(r.data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "This gift link isn't valid."));
  }, [token]);

  const next = encodeURIComponent(`/claim?t=${token}`);
  const claim = async () => {
    setBusy(true);
    try {
      const r = await api.post("/gifts/claim", { token });
      await refreshUser();
      toast.success(`Gift claimed. A present from ${r.data.from_name}: every Life Season is now yours.`);
      navigate("/timeline?celebrate=1");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "We couldn't claim this gift.");
      setBusy(false);
    }
  };

  const state = gift?.state;
  return (
    <div data-testid="claim-page" className="mx-auto max-w-xl px-5 py-10 sm:py-14">
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#E06D53]"><Gift size={16} weight="duotone" /> A gift for you</p>
      {error && <div className="mt-6"><Notice testid="claim-error" text={error} /></div>}
      {gift && (
        <>
          <h1 data-testid="claim-title" className="mt-2 font-serif text-4xl text-[#2D2824] sm:text-5xl">{gift.recipient_name}, {gift.from_name} has given you Lifetime Access</h1>
          {gift.occasion && <p className="mt-3 text-sm font-semibold text-[#C58F28]">{gift.occasion}</p>}
          {gift.message && <p data-testid="claim-message" className="mt-4 break-words font-serif text-xl italic leading-snug text-[#2D2824]">“{gift.message}”</p>}
          <p className="mt-4 flex gap-2 text-sm leading-relaxed text-[#6E665E]">
            <LockKey size={20} weight="duotone" className="shrink-0 text-[#36858B]" />
            You'll use your own account and password. Your diary is completely private: {gift.from_name} will never see your memories.
          </p>
          <div className="mt-8">
            {state !== "active" ? (
              <Notice testid="claim-state" text={(STATE_TEXT[state] || STATE_TEXT.expired)(gift)} />
            ) : user ? (
              user.is_pro ? (
                <Notice testid="claim-already-pro" text={`You're signed in as ${user.username || user.email}, which already has Lifetime Access. Sign out and use another account, or let ${gift.from_name} know so they can give it to someone else.`} />
              ) : (
                <div className="space-y-3">
                  <button data-testid="claim-gift-btn" onClick={claim} disabled={busy}
                    className="w-full rounded-full bg-[#E06D53] px-6 py-4 text-base font-semibold text-white hover:bg-[#C95A42] disabled:opacity-60">
                    {busy ? "Claiming…" : "Claim Your Gift"}
                  </button>
                  <p className="text-center text-sm text-[#8A7F76]">Claiming as <strong>{user.username || user.email}</strong></p>
                </div>
              )
            ) : (
              <div className="space-y-3">
                <button data-testid="claim-create-account-btn" onClick={() => navigate(`/account?mode=create&next=${next}`)}
                  className="w-full rounded-full bg-[#E06D53] px-6 py-4 text-base font-semibold text-white hover:bg-[#C95A42]">
                  Claim Your Gift · create my account
                </button>
                <button data-testid="claim-signin-btn" onClick={() => navigate(`/account?next=${next}`)}
                  className="w-full rounded-full border-2 border-[#36858B] px-6 py-3.5 text-base font-semibold text-[#36858B] hover:bg-[#EBF4F5]">
                  I already have an account · sign in
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
